import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import CommandLayout from "../../components/war-room/CommandLayout";
import { useLex } from "../../lib/theme/ThemeProvider";
import {
  useForgeStrikeArtifact, usePurgeStrikeArtifact, useDefensePrecedents,
  type StrikeArtifact, type MtdPair, type MtdCount, type MtdDefense, type MtdSynth, type MtdNotice, type GapItem,
  type AnswerResponse, type AnswerDefense, type AnswerCounterclaim, type DefenseEvidence, type DefensePrecedent, type AmendDiffItem,
} from "../../hooks/useStrikeArtifacts";
import { useChunkContext } from "../../hooks/useChunkContext";
import { downloadWord, printPdf } from "../../lib/exportMemo";
import CampaignRail from "../../components/war-room/CampaignRail";

/**
 * Berserkrs — strike artifacts. Each procedural stage gets an analysis and a
 * reviewable deliverable: draft → review → (counsel decides). Never auto-sent.
 * Artifacts: MTD memo, exhibit gap report, answer skeleton.
 */
function Berserkrs() {
  const { caseId } = useParams<{ caseId: string }>();
  const lex = useLex();
  const { forge, forging, error, artifacts, loading, listError } = useForgeStrikeArtifact(caseId);
  const { purge, purging, purgeError } = usePurgeStrikeArtifact(caseId);
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmPurgeId, setConfirmPurgeId] = useState<string | null>(null);

  const open = artifacts.find((a) => a.id === openId) ?? artifacts[0] ?? null;

  return (
    <CommandLayout caseId={caseId} variant="berserkr" currentCommand="berserkrs">
      <header className="rounded border border-steel-dark bg-iron/70 px-6 py-6 backdrop-blur-sm">
        <p className="mb-2 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">{`// ${lex.commands.berserkrs.verb.toLowerCase()}`}</p>
        <h2 className="font-display text-2xl font-light leading-tight tracking-tight text-bone md:text-3xl"
            style={{ textShadow: "0 0 24px rgba(195,35,35,0.35), 0 2px 16px rgba(0,0,0,0.6)" }}>
          Strike artifacts. Drafted here, reviewed by counsel.
        </h2>
        <p className="mt-3 max-w-3xl font-display text-sm leading-relaxed text-bone-dim">
          Each procedural stage forges its own deliverable from the war room's intelligence.
          Everything is a draft for your attorney — nothing leaves this room on its own.
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            disabled={!caseId || forging}
            onClick={() => forge("mtd")}
            className={`rounded border px-3 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] transition-colors ${
              forging
                ? "border-ember-bright/40 bg-ember/10 text-ember-bright"
                : "border-ember-bright/50 bg-ember/10 text-bone hover:bg-ember/30 hover:text-ember-bright"
            }`}
          >
            {forging ? "forging…" : "⚒ forge: motion-to-dismiss memo"}
          </button>
          <button
            type="button"
            disabled={!caseId || forging}
            onClick={() => forge("gaps")}
            className={`rounded border px-3 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] transition-colors ${
              forging
                ? "border-blue-bright/40 bg-iron-2/60 text-blue-bright"
                : "border-blue-bright/50 bg-iron-2/60 text-bone hover:bg-iron-2 hover:text-blue-bright"
            }`}
          >
            {forging ? "forging…" : "⚒ forge: missing-exhibits report"}
          </button>
          <button
            type="button"
            disabled={!caseId || forging}
            onClick={() => forge("answer")}
            className={`rounded border px-3 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] transition-colors ${
              forging
                ? "border-ember-bright/40 bg-ember/10 text-ember-bright"
                : "border-ember-bright/50 bg-ember/10 text-bone hover:bg-ember/30 hover:text-ember-bright"
            }`}
          >
            {forging ? "forging…" : "⚒ forge: answer skeleton"}
          </button>
          <button
            type="button"
            disabled={!caseId || forging}
            onClick={() => forge("amend")}
            className={`rounded border px-3 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] transition-colors ${
              forging
                ? "border-blue-bright/40 bg-iron-2/60 text-blue-bright"
                : "border-blue-bright/50 bg-iron-2/60 text-bone hover:bg-iron-2 hover:text-blue-bright"
            }`}
          >
            {forging ? "forging…" : "⚒ forge: amendment diff"}
          </button>
        </div>
        {error && <p className="mt-2 font-display text-base text-ember-bright">// forge failed: {error}</p>}
        {forging && (
          <p className="mt-2 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">
            // forging in the background — the answer skeleton works all 165 paragraphs, give it a minute or two…
          </p>
        )}
      </header>

      <CampaignRail caseId={caseId} />

      <div className="rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
        <div className="flex items-center justify-between border-b border-steel-dark/70 px-5 py-3 font-display text-base font-light uppercase tracking-[0.16em]">
          <span className="text-bone-dim">the armory // artifacts</span>
          <span className="text-bone-mute/70">{artifacts.length} forged</span>
        </div>
        <div className="px-5 py-4">
          {loading && <p className="font-display text-base text-bone-mute">// opening the armory…</p>}
          {listError && <p className="font-display text-base text-ember-bright">// {listError.message}</p>}
          {!loading && artifacts.length === 0 && (
            <p className="font-display text-base text-bone-mute">// nothing forged yet — strike the anvil above</p>
          )}
          {purgeError && <p className="mb-2 font-display text-base text-ember-bright">// purge failed: {purgeError}</p>}
          <ul className="space-y-1.5">
            {artifacts.map((a) => (
              <li key={a.id} className="flex items-stretch gap-1.5">
                <button
                  type="button"
                  onClick={() => setOpenId(a.id)}
                  className={`min-w-0 flex-1 rounded border px-3 py-2 text-left font-display text-[13px] transition-colors ${
                    open?.id === a.id ? "border-ember-bright/50 bg-ember/10 text-bone" : "border-steel-dark/70 bg-black/20 text-bone-dim hover:border-ember-bright/40"
                  }`}
                >
                  <span className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute">{a.artifact_kind.replace(/_/g, " ")} · {new Date(a.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}{a.content?.build ? ` · ${a.content.build}` : ""}</span>
                  <span className="block truncate">{a.title}</span>
                </button>
                {confirmPurgeId === a.id ? (
                  <span className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      disabled={purging === a.id}
                      onClick={async () => { await purge(a.id); setConfirmPurgeId(null); if (openId === a.id) setOpenId(null); }}
                      className="rounded border border-ember-bright/60 bg-ember/20 px-2 font-display text-sm font-light uppercase tracking-[0.08em] text-ember-bright hover:bg-ember/40"
                      title="permanently delete this artifact"
                    >
                      {purging === a.id ? "purging…" : "confirm purge"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmPurgeId(null)}
                      className="rounded border border-steel-dark px-2 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute hover:text-bone"
                    >
                      keep
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmPurgeId(a.id)}
                    className="shrink-0 rounded border border-steel-dark/70 px-2.5 font-display text-base text-bone-mute/70 transition-colors hover:border-ember-bright/50 hover:text-ember-bright"
                    title="purge this artifact (asks to confirm)"
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {open && <MemoViewer a={open} caseId={caseId} />}
    </CommandLayout>
  );
}

/* ============================ memo viewer ============================ */

const STRENGTH_COLOR: Record<string, string> = { strong: "#e8703a", moderate: "#c9a23a" };
const VERDICT_COLOR: Record<string, string> = {
  vulnerable: "#5a8a5a", partially_vulnerable: "#c9a23a", likely_survives: "#b3503f",
  bars: "#5a8a5a", arguable: "#c9a23a", no_bar: "#6b7480",
  likely_dismissed: "#5a8a5a", likely_narrowed: "#c9a23a",
};
const vc = (k: string): string => VERDICT_COLOR[k] ?? "#5a6470";
const RESPONSE_COLOR: Record<string, string> = {
  admit: "#5a8a5a", deny: "#b3503f", admit_in_part: "#c9a23a",
  insufficient_knowledge: "#5b9bd5", no_response_required: "#6b7480",
};
const rc = (k: string): string => RESPONSE_COLOR[k] ?? "#5a6470";

/** Strip raw [A#]/[E#] tag clusters the model may have left in prose fields —
 *  the resolved supports/evidence arrays are the real citations. */
const stripTags = (t: string | undefined | null): string =>
  (t ?? "").replace(/\s*\[(?:\s*[AE]\d+\s*[,;]?\s*)+\]/g, "").replace(/\s{2,}/g, " ").trim();

function MemoViewer({ a, caseId }: { a: StrikeArtifact; caseId: string | undefined }) {
  const c = a.content;
  return (
    <div className="rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-steel-dark/70 bg-black/30 px-6 py-4">
        <div>
          <p className="font-display text-base font-light uppercase tracking-[0.3em] text-bone-mute">draft · attorney work product</p>
          <h3 className="mt-1 font-display text-xl font-light text-bone">{a.title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => downloadWord(a)}
            className="rounded border border-blue-bright/50 bg-iron-2/60 px-3 py-1.5 font-display text-base font-light uppercase tracking-[0.2em] text-blue-bright hover:bg-iron-2">
            ↧ download word
          </button>
          <button type="button" onClick={() => printPdf(a)}
            className="rounded border border-ember-bright/50 bg-ember/10 px-3 py-1.5 font-display text-base font-light uppercase tracking-[0.2em] text-ember-bright hover:bg-ember/30">
            ⎙ print / save pdf
          </button>
        </div>
      </div>

      <div className="space-y-6 px-6 py-5">
        {c.basis && <p className="border-l-2 border-steel-dark pl-3 font-display text-[12px] italic text-bone-dim">{c.basis}</p>}

        {(c.prongs ?? []).map((p) => (
          <ProngSection key={p.key} title={p.title} intro={p.intro} count={(p.items ?? []).length}>
            <div className="mt-3 space-y-2">
              {p.key === "exhibit_contradiction" && (p.items as MtdPair[]).map((i, k) => (
                <div key={k} className="rounded border border-steel-dark/60 bg-black/20 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-base font-light uppercase tracking-[0.2em] text-bone-mute">{i.allegation_ref}</span>
                    <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em]"
                          style={{ background: STRENGTH_COLOR[i.strength] ?? "#5a6470", color: "#0c0f12" }}>{i.strength}</span>
                  </div>
                  <p className="mt-1 font-display text-[13px] italic text-bone">&ldquo;{i.allegation}&rdquo;</p>
                  <p className="mt-2 font-display text-[12px] text-bone-dim">
                    <span className="text-ember-soft">{i.instrument}</span> — {i.provision}
                  </p>
                  <p className="mt-0.5 font-display text-base italic text-bone-mute">{i.grant_text}</p>
                  <p className="mt-1.5 font-display text-[12px] leading-snug text-bone-dim">{i.effect}</p>
                </div>
              ))}
              {p.key === "legal_sufficiency" && (p.items as MtdCount[]).map((i, k) => (
                <div key={k} className="rounded border border-steel-dark/60 bg-black/20 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-[13px] font-medium text-bone">Count {i.ordinal} — {i.count_name}</span>
                    <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em]" style={{ background: vc(i.verdict), color: "#0c0f12" }}>
                      {i.verdict.replace(/_/g, " ")}
                    </span>
                  </div>
                  {i.defects.length > 0 && (
                    <ul className="mt-1.5 list-inside list-disc font-display text-[12px] text-bone-dim">
                      {i.defects.map((d, j) => <li key={j}>{d}</li>)}
                    </ul>
                  )}
                  <p className="mt-1.5 font-display text-[12px] leading-snug text-bone-mute">{i.rationale}</p>
                </div>
              ))}
              {p.key === "judicial_notice" && (p.items as MtdNotice[]).map((i, k) => (
                <div key={k} className="rounded border border-steel-dark/60 bg-black/20 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-base font-light uppercase tracking-[0.2em] text-bone-mute">{i.allegation_ref}</span>
                    <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em]" style={{ background: i.strength === "strong" ? "#e8703a" : "#c9a23a", color: "#0c0f12" }}>{i.strength}</span>
                    <span className="rounded border border-blue-bright/50 px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.14em] text-blue-bright">{i.notice_basis.replace(/_/g, " ")}</span>
                  </div>
                  <p className="mt-1 font-display text-[13px] italic text-bone">&ldquo;{i.allegation}&rdquo;</p>
                  <p className="mt-2 font-display text-[12px] text-bone-dim"><span className="text-ember-soft">{i.record}</span></p>
                  {i.quote && <p className="mt-1 border-l-2 border-steel-dark pl-2 font-display text-base italic text-bone-mute">{i.quote}</p>}
                  <p className="mt-1.5 font-display text-[12px] leading-snug text-bone-dim">{i.effect}</p>
                </div>
              ))}
              {p.key === "gaps" && (p.items as GapItem[]).map((i, k) => (
                <div key={k} className="rounded border border-steel-dark/60 bg-black/20 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-[13px] font-medium text-bone">{i.reference}</span>
                    <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em]" style={{ background: i.in_corpus ? "#5a8a5a" : "#b3503f", color: "#0c0f12" }}>
                      {i.in_corpus ? "in corpus" : "missing"}
                    </span>
                    {i.public_record && (
                      <span className="rounded border border-blue-bright/50 px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.14em] text-blue-bright">public record · noticeable</span>
                    )}
                    <span className="font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute">{i.cited_at}</span>
                  </div>
                  <p className="mt-1 font-display text-[12px] text-bone-dim">{i.description}</p>
                  {i.in_corpus && i.matched_title && <p className="mt-0.5 font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute">matched: {i.matched_title}</p>}
                  {!i.in_corpus && <p className="mt-0.5 font-display text-base text-ember-soft">obtain from: {i.source_hint}</p>}
                </div>
              ))}
              {p.key === "amendment_diff" && (p.items as AmendDiffItem[]).map((i, k) => (
                <AmendDiffCard key={k} item={i} />
              ))}
              {p.key === "responses" && (p.items as AnswerResponse[]).map((i, k) => (
                <div key={k} className="rounded border border-steel-dark/60 bg-black/20 px-4 py-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-base font-light uppercase tracking-[0.2em] text-bone-mute">¶{i.paragraph_number}</span>
                    <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em]"
                          style={{ background: rc(i.response), color: "#0c0f12" }}>{i.response.replace(/_/g, " ")}</span>
                  </div>
                  {i.detail && <p className="mt-1 font-display text-[12px] text-bone">{i.detail}</p>}
                  <p className="mt-1 font-display text-[12px] leading-snug text-bone-dim">{i.rationale}</p>
                  {i.evidence_anchor && (
                    <p className="mt-1 border-l-2 border-steel-dark pl-2 font-display text-base italic text-bone-mute">{i.evidence_anchor}</p>
                  )}
                </div>
              ))}
              {p.key === "affirmative_defenses" && (p.items as AnswerDefense[]).map((i, k) => (
                <DefenseCard key={k} d={i} caseId={caseId} />
              ))}
              {p.key === "counterclaims" && (p.items as AnswerCounterclaim[]).map((i, k) => (
                <CounterclaimCard key={k} cc={i} caseId={caseId} />
              ))}
              {p.key === "facial_defenses" && (p.items as MtdDefense[]).map((i, k) => (
                <div key={k} className="rounded border border-steel-dark/60 bg-black/20 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-[13px] font-medium text-bone">{i.defense}</span>
                    <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em]" style={{ background: vc(i.assessment), color: "#0c0f12" }}>
                      {i.assessment.replace(/_/g, " ")}
                    </span>
                    <span className="font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute">applies: {i.applies_to}</span>
                  </div>
                  <p className="mt-1.5 font-display text-[12px] leading-snug text-bone-mute">{i.rationale}</p>
                </div>
              ))}
              {p.key === "synthesis" && (p.items as MtdSynth[]).map((i, k) => (
                <div key={k} className="rounded border border-steel-dark/60 bg-black/20 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-[13px] font-medium text-bone">Count {i.ordinal} — {i.count_name}</span>
                    <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em]" style={{ background: vc(i.predicted_outcome), color: "#0c0f12" }}>
                      {i.predicted_outcome.replace(/_/g, " ")}
                    </span>
                  </div>
                  {i.falling_theories.length > 0 && (
                    <ul className="mt-1.5 list-inside list-disc font-display text-[12px] text-bone-dim">
                      {i.falling_theories.map((d, j) => <li key={j}>falls: {d}</li>)}
                    </ul>
                  )}
                  <p className="mt-1.5 font-display text-[12px] text-bone"><span className="text-ember-soft">survives:</span> {i.surviving_theory}</p>
                  <p className="mt-0.5 font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute">drivers: {i.drivers}</p>
                  <p className="mt-1.5 font-display text-[12px] leading-snug text-bone-mute">{i.reasoning}</p>
                </div>
              ))}
            </div>
          </ProngSection>
        ))}

        {c.caveats && <p className="border-t border-steel-dark/70 pt-3 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute/70">{c.caveats}</p>}
      </div>
    </div>
  );
}

/* ===================== collapsible prong section ===================== */

/**
 * Collapsible artifact section. Collapsed by default — the answer skeleton's
 * response map alone is 165 cards, so the page opens as a table of contents.
 */
function ProngSection({ title, intro, count, children }: {
  title: string; intro: string | undefined; count: number; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded border border-steel-dark/50 bg-black/10">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-black/20"
      >
        <span className={`font-display text-base transition-transform ${open ? "rotate-90" : ""}`}>▸</span>
        <h4 className="font-display text-base font-light text-bone">{title}</h4>
        <span className="ml-auto rounded border border-steel-dark px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">
          {count} {count === 1 ? "item" : "items"}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          {intro && <p className="font-display text-[12px] leading-snug text-bone-dim">{intro}</p>}
          {children}
        </div>
      )}
    </section>
  );
}

/* ===================== precedent matching + card ===================== */

const OUTCOME_STYLE: Record<string, { label: string; bg: string }> = {
  defense_succeeded: { label: "defense won", bg: "#5a8a5a" },
  defense_failed: { label: "defense failed", bg: "#b3503f" },
  claim_survived: { label: "claim survived — defense too weak here", bg: "#c9a23a" },
};

/** Match precedents to a defense/claim by keyword overlap with its name. */
function matchPrecedents(name: string, all: DefensePrecedent[]): DefensePrecedent[] {
  const hay = name.toLowerCase();
  return all.filter((p) => p.keywords.some((k) => hay.includes(k.toLowerCase())));
}

/** A single verified precedent — citation, holding, takeaway, source link. */
function PrecedentCard({ p }: { p: DefensePrecedent }) {
  const [open, setOpen] = useState(false);
  const o = OUTCOME_STYLE[p.outcome] ?? { label: p.outcome, bg: "#5a6470" };
  return (
    <div className="rounded border border-blue-bright/30 bg-iron-2/40 px-3 py-2">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 text-left">
        <span className="font-display text-base text-blue-bright">{open ? "▾" : "▸"}</span>
        <span className="font-display text-[12px] font-medium text-bone">{p.case_name}</span>
        <span className="font-display text-sm font-light uppercase tracking-[0.14em] text-blue-bright/80">{p.citation}</span>
        <span className="ml-auto rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.14em]" style={{ background: o.bg, color: "#0c0f12" }}>{o.label}</span>
      </button>
      {open && (
        <div className="mt-1.5 space-y-1.5 pl-4">
          <p className="font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute">{p.court}{p.posture ? ` · ${p.posture}` : ""}</p>
          <div>
            <p className="font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">held</p>
            <p className="mt-0.5 font-display text-[12px] leading-snug text-bone-dim">{p.holding}</p>
          </div>
          <div>
            <p className="font-display text-sm font-light uppercase tracking-[0.08em] text-ember-soft">why it matters here</p>
            <p className="mt-0.5 font-display text-[12px] leading-snug text-bone">{p.takeaway}</p>
          </div>
          <a href={p.source_url} target="_blank" rel="noopener noreferrer"
             className="inline-block font-display text-sm font-light uppercase tracking-[0.14em] text-blue-bright hover:text-blue-bright/80">
            ↗ read the opinion (nccourts.gov) · verified {p.verified_on}
          </a>
        </div>
      )}
    </div>
  );
}

/** Collapsible "real example" block attached to a defense/counterclaim card. */
function PrecedentBlock({ name }: { name: string }) {
  const { data: all } = useDefensePrecedents();
  const matches = matchPrecedents(name, all ?? []);
  if (matches.length === 0) return null;
  return (
    <div className="mt-2 rounded border border-blue-bright/30 bg-black/20 px-2.5 py-2">
      <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-blue-bright">▣ how this defense has played out — real NC cases</p>
      <div className="mt-1.5 space-y-1.5">
        {matches.map((p) => <PrecedentCard key={p.id} p={p} />)}
      </div>
    </div>
  );
}

/* ===================== defense card + hover dossier ===================== */

/**
 * Affirmative-defense card. Hovering reveals the dossier popup (definition,
 * elements to prove, supporting evidence); clicking "dossier" pins it open so
 * the evidence links inside are comfortably clickable.
 */
function DefenseCard({ d, caseId }: { d: AnswerDefense; caseId: string | undefined }) {
  const lex = useLex();
  const [pinned, setPinned] = useState(false);
  const [openEvidence, setOpenEvidence] = useState<number | null>(null);
  const hasDossier = !!(d.definition || (d.must_prove?.length ?? 0) > 0 || (d.evidence?.length ?? 0) > 0);

  return (
    <div className="group relative rounded border border-steel-dark/60 bg-black/20 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-display text-[13px] font-medium text-bone">{d.defense}</span>
        <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em]"
              style={{ background: d.strength === "strong" ? "#e8703a" : "#c9a23a", color: "#0c0f12" }}>{d.strength}</span>
        <span className="font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute">applies: {d.applies_to}</span>
        {hasDossier && (
          <button
            type="button"
            onClick={() => setPinned((p) => !p)}
            className={`ml-auto rounded border px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em] transition-colors ${
              pinned ? "border-ember-bright/60 bg-ember/20 text-ember-bright" : "border-steel-dark text-bone-mute hover:border-ember-bright/50 hover:text-ember-bright"
            }`}
          >
            {pinned ? "▾ close dossier" : "ⓘ dossier"}
          </button>
        )}
      </div>
      {d.grounds && (
        <p className="mt-1.5 font-display text-[12px] leading-snug text-bone">
          <span className="font-display text-sm font-light uppercase tracking-[0.08em] text-ember-soft">grounds // </span>
          {stripTags(d.grounds)}
        </p>
      )}
      <p className="mt-1.5 font-display text-[12px] leading-snug text-bone-dim">{stripTags(d.basis)}</p>
      {(d.supports?.length ?? 0) > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">supported by:</span>
          {d.supports!.map((s, j) => (
            <Link
              key={j}
              to={caseId ? `/case/${caseId}/shield-wall` : "#"}
              title={`${s.title}${s.locator ? ` — ${s.locator}` : ""} (${lex.ui.viewInShieldWall})`}
              className="rounded border border-blue-bright/50 px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.14em] text-blue-bright hover:bg-iron-2"
            >
              ⛨ {s.label}{s.locator ? ` · ${s.locator}` : ""}
            </Link>
          ))}
        </div>
      )}
      {(d.evidence?.length ?? 0) > 0 && (
        <div className="mt-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">corroborating evidence:</span>
            {d.evidence!.map((e, j) => (
              <button
                key={j}
                type="button"
                onClick={() => setOpenEvidence((cur) => (cur === j ? null : j))}
                title={e.quote.slice(0, 140)}
                className={`rounded border px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.14em] transition-colors ${
                  openEvidence === j
                    ? "border-ember-bright/60 bg-ember/20 text-ember-bright"
                    : "border-ember-bright/40 text-ember-soft hover:bg-ember/10 hover:text-ember-bright"
                }`}
              >
                ✉ {e.source_label ?? "evidence"}{e.occurred_on ? ` · ${e.occurred_on}` : ""}
              </button>
            ))}
          </div>
          {openEvidence !== null && d.evidence![openEvidence] && (
            <div className="mt-1.5">
              <DefenseEvidenceRow e={d.evidence![openEvidence]} />
            </div>
          )}
        </div>
      )}

      <PrecedentBlock name={d.defense} />

      {/* dossier — appears on hover, pins on click */}
      {hasDossier && (
        <div
          className={`absolute left-2 right-2 top-full z-30 mt-1 rounded border border-ember-bright/40 bg-charcoal/95 px-4 py-3 shadow-ember-soft backdrop-blur-md ${
            pinned ? "block" : "hidden group-hover:block"
          }`}
        >
          <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-ember-soft">// the dossier — {d.defense}</p>
          {d.definition && (
            <div className="mt-2">
              <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute">what it is</p>
              <p className="mt-0.5 font-display text-[12px] leading-snug text-bone">{d.definition}</p>
            </div>
          )}
          {(d.must_prove?.length ?? 0) > 0 && (
            <div className="mt-2.5">
              <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute">what must be proven</p>
              <ul className="mt-1 space-y-0.5">
                {d.must_prove.map((m, j) => (
                  <li key={j} className="flex gap-2 font-display text-[12px] leading-snug text-bone-dim">
                    <span className="shrink-0 text-ember-soft">{j + 1}.</span>
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(d.evidence?.length ?? 0) > 0 && (
            <div className="mt-2.5">
              <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute">supporting evidence — click to open the source</p>
              <div className="mt-1.5 space-y-1.5">
                {d.evidence!.map((e, j) => <DefenseEvidenceRow key={j} e={e} />)}
              </div>
            </div>
          )}
          {!pinned && (
            <p className="mt-2.5 border-t border-steel-dark/60 pt-1.5 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute/60">
              // click "dossier" to pin this open
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DefenseEvidenceRow({ e }: { e: DefenseEvidence }) {
  const [open, setOpen] = useState(false);
  const ctx = useChunkContext(e.chunk_id, open);
  return (
    <div className="rounded border border-steel-dark/60 bg-black/30 px-3 py-2">
      <div className="mb-1 flex flex-wrap items-center gap-2 font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute/80">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-ember-bright" />
        <span>{e.source_label ?? "evidence"}</span>
        {e.occurred_on && <span>· {e.occurred_on}</span>}
        {e.paragraph_number && <span>· refutes ¶{e.paragraph_number}</span>}
      </div>
      {e.quote && (
        <p className="border-l-2 border-steel-dark pl-2 font-display text-[12px] leading-snug text-bone-dim">{e.quote}</p>
      )}
      {e.chunk_id && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-1.5 font-display text-sm font-light uppercase tracking-[0.1em] text-blue-bright/80 hover:text-blue-bright"
        >
          {open ? "▾ hide source" : "▸ view in source"}
        </button>
      )}
      {open && (
        <div className="mt-2 rounded border border-steel-dark/50 bg-charcoal/60 p-2">
          {ctx.isLoading && <p className="font-display text-sm text-bone-mute">// opening the source…</p>}
          {ctx.error && <p className="font-display text-sm text-ember-bright">// {ctx.error.message}</p>}
          {ctx.data && (
            <div>
              <p className="mb-1.5 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute/70">
                {ctx.data.source_label}{ctx.data.evidence_kind ? ` · ${ctx.data.evidence_kind.replace(/_/g, " ")}` : ""}
              </p>
              <div className="space-y-1">
                {ctx.data.chunks.map((line) => (
                  <pre
                    key={line.chunk_index}
                    className={`overflow-x-auto whitespace-pre-wrap rounded px-2 py-1 font-display text-base leading-snug ${
                      line.is_center ? "bg-ember/10 text-bone shadow-ember-soft" : "text-bone-mute/70"
                    }`}
                  >
                    {line.body}
                  </pre>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ===================== counterclaim card + dossier ===================== */

/**
 * Counterclaim candidate card — same treatment as defenses: grounds, instrument
 * chips, clickable corroborating evidence, hover/pin dossier with definition
 * and elements. Legacy artifacts (free-text evidence_basis) still render.
 */
function CounterclaimCard({ cc, caseId }: { cc: AnswerCounterclaim; caseId: string | undefined }) {
  const lex = useLex();
  const [pinned, setPinned] = useState(false);
  const [openEvidence, setOpenEvidence] = useState<number | null>(null);
  const hasDossier = !!(cc.definition || (cc.must_prove?.length ?? 0) > 0 || (cc.evidence?.length ?? 0) > 0);

  return (
    <div className="group relative rounded border border-steel-dark/60 bg-black/20 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-display text-[13px] font-medium text-bone">{cc.claim_name}</span>
        {cc.compulsory ? (
          <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em]" style={{ background: "#b3503f", color: "#0c0f12" }}>
            compulsory — rule 13(a)
          </span>
        ) : (
          <span className="rounded border border-steel-dark px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">permissive</span>
        )}
        {hasDossier && (
          <button
            type="button"
            onClick={() => setPinned((v) => !v)}
            className={`ml-auto rounded border px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em] transition-colors ${
              pinned ? "border-ember-bright/60 bg-ember/20 text-ember-bright" : "border-steel-dark text-bone-mute hover:border-ember-bright/50 hover:text-ember-bright"
            }`}
          >
            {pinned ? "▾ close dossier" : "ⓘ dossier"}
          </button>
        )}
      </div>
      <p className="mt-1.5 font-display text-[12px] leading-snug text-bone-dim">{stripTags(cc.theory)}</p>
      {cc.grounds && (
        <p className="mt-1.5 font-display text-[12px] leading-snug text-bone">
          <span className="font-display text-sm font-light uppercase tracking-[0.08em] text-ember-soft">grounds // </span>
          {stripTags(cc.grounds)}
        </p>
      )}
      <p className="mt-1 font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute">basis: {stripTags(cc.basis)}</p>
      {cc.evidence_basis && (cc.evidence?.length ?? 0) === 0 && (
        <p className="mt-1 border-l-2 border-steel-dark pl-2 font-display text-base italic text-bone-mute">{stripTags(cc.evidence_basis)}</p>
      )}
      {cc.caution && <p className="mt-1 font-display text-base text-ember-soft">⚠ {stripTags(cc.caution)}</p>}
      {(cc.supports?.length ?? 0) > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">supported by:</span>
          {cc.supports!.map((s, j) => (
            <Link
              key={j}
              to={caseId ? `/case/${caseId}/shield-wall` : "#"}
              title={`${s.title}${s.locator ? ` — ${s.locator}` : ""} (${lex.ui.viewInShieldWall})`}
              className="rounded border border-blue-bright/50 px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.14em] text-blue-bright hover:bg-iron-2"
            >
              ⛨ {s.label}{s.locator ? ` · ${s.locator}` : ""}
            </Link>
          ))}
        </div>
      )}
      {(cc.evidence?.length ?? 0) > 0 && (
        <div className="mt-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">corroborating evidence:</span>
            {cc.evidence!.map((e, j) => (
              <button
                key={j}
                type="button"
                onClick={() => setOpenEvidence((cur) => (cur === j ? null : j))}
                title={e.quote.slice(0, 140)}
                className={`rounded border px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.14em] transition-colors ${
                  openEvidence === j
                    ? "border-ember-bright/60 bg-ember/20 text-ember-bright"
                    : "border-ember-bright/40 text-ember-soft hover:bg-ember/10 hover:text-ember-bright"
                }`}
              >
                ✉ {e.source_label ?? "evidence"}{e.occurred_on ? ` · ${e.occurred_on}` : ""}
              </button>
            ))}
          </div>
          {openEvidence !== null && cc.evidence![openEvidence] && (
            <div className="mt-1.5">
              <DefenseEvidenceRow e={cc.evidence![openEvidence]} />
            </div>
          )}
        </div>
      )}

      <PrecedentBlock name={cc.claim_name} />

      {hasDossier && (
        <div
          className={`absolute left-2 right-2 top-full z-30 mt-1 rounded border border-ember-bright/40 bg-charcoal/95 px-4 py-3 shadow-ember-soft backdrop-blur-md ${
            pinned ? "block" : "hidden group-hover:block"
          }`}
        >
          <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-ember-soft">// the dossier — {cc.claim_name}</p>
          {cc.definition && (
            <div className="mt-2">
              <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute">what it is</p>
              <p className="mt-0.5 font-display text-[12px] leading-snug text-bone">{stripTags(cc.definition)}</p>
            </div>
          )}
          {(cc.must_prove?.length ?? 0) > 0 && (
            <div className="mt-2.5">
              <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute">what must be proven</p>
              <ul className="mt-1 space-y-0.5">
                {cc.must_prove!.map((m, j) => (
                  <li key={j} className="flex gap-2 font-display text-[12px] leading-snug text-bone-dim">
                    <span className="shrink-0 text-ember-soft">{j + 1}.</span>
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(cc.evidence?.length ?? 0) > 0 && (
            <div className="mt-2.5">
              <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute">supporting evidence — click to open the source</p>
              <div className="mt-1.5 space-y-1.5">
                {cc.evidence!.map((e, j) => <DefenseEvidenceRow key={j} e={e} />)}
              </div>
            </div>
          )}
          {!pinned && (
            <p className="mt-2.5 border-t border-steel-dark/60 pt-1.5 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute/60">
              // click "dossier" to pin this open
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ===================== amendment diff card ===================== */

const DIFF_KIND_LABEL: Record<string, string> = {
  dropped_allegation: "dropped allegation", modified_allegation: "modified allegation",
  added_allegation: "added allegation", dropped_exhibit: "dropped exhibit",
  added_exhibit: "added exhibit", count_change: "count change", party_change: "party change",
};
const DIFF_SEV_COLOR: Record<string, string> = { high: "#b3503f", notable: "#c9a23a", minor: "#6b7480" };

function AmendDiffCard({ item }: { item: AmendDiffItem }) {
  const [openEvidence, setOpenEvidence] = useState<number | null>(null);
  return (
    <div className="rounded border border-steel-dark/60 bg-black/20 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em]"
              style={{ background: DIFF_SEV_COLOR[item.severity] ?? "#5a6470", color: "#0c0f12" }}>{item.severity}</span>
        <span className="rounded border border-steel-dark px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">
          {DIFF_KIND_LABEL[item.kind] ?? item.kind.replace(/_/g, " ")}
        </span>
        {item.amended_ref && <span className="font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute">amended {item.amended_ref}</span>}
      </div>
      {item.original_text && (
        <div className="mt-2">
          <p className="font-display text-sm font-light uppercase tracking-[0.08em] text-ember-soft">sworn original</p>
          <p className="mt-0.5 border-l-2 border-ember-bright/40 pl-2 font-display text-[12px] italic leading-snug text-bone">{item.original_text}</p>
        </div>
      )}
      <div className="mt-2">
        <p className="font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">{item.amended_text ? "as amended" : "— gone from the amendment —"}</p>
        {item.amended_text && (
          <p className="mt-0.5 border-l-2 border-steel-dark pl-2 font-display text-[12px] leading-snug text-bone-dim">{item.amended_text}</p>
        )}
      </div>
      <p className="mt-2 font-display text-[12px] leading-snug text-bone">
        <span className="font-display text-sm font-light uppercase tracking-[0.08em] text-ember-soft">why it matters // </span>
        {item.significance}
      </p>
      {(item.evidence?.length ?? 0) > 0 && (
        <div className="mt-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">in the original:</span>
            {item.evidence!.map((e, j) => (
              <button
                key={j}
                type="button"
                onClick={() => setOpenEvidence((cur) => (cur === j ? null : j))}
                title={e.quote.slice(0, 140)}
                className={`rounded border px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.14em] transition-colors ${
                  openEvidence === j
                    ? "border-ember-bright/60 bg-ember/20 text-ember-bright"
                    : "border-ember-bright/40 text-ember-soft hover:bg-ember/10 hover:text-ember-bright"
                }`}
              >
                ⚖ view sworn text {j + 1}
              </button>
            ))}
          </div>
          {openEvidence !== null && item.evidence![openEvidence] && (
            <div className="mt-1.5">
              <DefenseEvidenceRow e={item.evidence![openEvidence]} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Berserkrs;
